import * as React from "react";
import { Loader2, Pencil, Trash2 } from "lucide-react";

import { toastError, presentMutationError } from "../../../../utils/showApiErrorToast";
import {
  BROKER_ALERT_TITLE_CLASS,
  BROKER_FIELD_DESC_CLASS,
} from "@/components/Broker/brokerAlertUi";
import {
  accountSettingsFieldValueClassName,
  accountSettingsNestedCardClassName,
} from "../accountSettingsCardShell";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { patchPnlMilestone } from "../../../../services/milestonesApi";
import type {
  PnlMilestoneDefinition,
  PnlMilestoneDirection,
  PnlMilestoneScope,
  PnlMilestoneType,
} from "../../../../types/milestones";
import { PnlMilestoneFieldSelect } from "./PnlMilestoneFieldSelect";
import {
  clampMilestoneProgress01,
  formatMilestonePercent,
  milestoneProgressGradientInnerWidthPercent,
  PNL_MILESTONE_DIRECTION_OPTIONS,
  PNL_MILESTONE_SCOPE_OPTIONS,
  PNL_MILESTONE_TYPE_OPTIONS,
  pnlMilestoneRowCardContentClassName,
} from "./pnlMilestonesUi";

export interface PnlMilestoneDefinitionRowProps {
  milestone: PnlMilestoneDefinition;
  isEditing: boolean;
  draft: Partial<PnlMilestoneDefinition> | undefined;
  saving: boolean;
  formatMoney: (value: number) => string;
  onDraftChange: (patch: Partial<PnlMilestoneDefinition>) => void;
  onBeginEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onDelete: () => void;
  onReloadQuiet: (accountCode: string) => Promise<void>;
  setSaving: (value: boolean) => void;
}

export function PnlMilestoneDefinitionRow({
  milestone: m,
  isEditing,
  draft,
  saving,
  formatMoney,
  onDraftChange,
  onBeginEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
  onReloadQuiet,
  setSaving,
}: PnlMilestoneDefinitionRowProps): React.ReactElement {
  const d = (isEditing ? draft : m) ?? m;
  const typeLabel =
    d.milestone_type === "percent"
      ? formatMilestonePercent(Number(d.threshold_value ?? 0))
      : formatMoney(Number(d.threshold_value ?? 0));
  const isAuto = Boolean(m.is_auto ?? m.is_system);
  const progressUnit = m.progress_unit ?? null;
  const progressCurrent = m.progress_current ?? null;
  const progressTarget = m.progress_target ?? null;
  const progressFractionFromApi =
    typeof m.progress_fraction === "number" && Number.isFinite(m.progress_fraction)
      ? m.progress_fraction
      : null;
  const progressFractionDerived =
    progressCurrent != null &&
    progressTarget != null &&
    typeof progressCurrent === "number" &&
    typeof progressTarget === "number" &&
    progressTarget !== 0
      ? progressCurrent / progressTarget
      : null;
  const progressFractionForBar = progressFractionFromApi ?? progressFractionDerived;
  const underwaterFromCurrent =
    typeof progressCurrent === "number" &&
    progressCurrent < 0 &&
    typeof progressTarget === "number" &&
    progressTarget > 0;
  const progressBarValue = (() => {
    if (underwaterFromCurrent) return 0;
    const fraction = progressFractionForBar;
    if (fraction != null && fraction < 0) return 0;
    if (fraction != null) return Math.round(clampMilestoneProgress01(fraction) * 100);
    return 0;
  })();
  const reached = Boolean(m.reached);
  const distance = m.distance_to_target ?? null;
  const showProgressSection =
    progressFractionFromApi != null ||
    progressFractionDerived != null ||
    distance != null ||
    isAuto;
  const formatProgressValue = (value: number) =>
    progressUnit === "pct" ? formatMilestonePercent(value) : formatMoney(value);
  const gradientInnerWidthPercent = milestoneProgressGradientInnerWidthPercent(progressBarValue);
  const showEnabledToggle = isAuto && !reached;
  const showCustomActions = !isAuto;
  const showFooter = showEnabledToggle || showCustomActions || isEditing;

  return (
    <div
      data-pnl-milestone-row=""
      className={cn(accountSettingsNestedCardClassName)}
    >
      <div className={pnlMilestoneRowCardContentClassName}>
        <Field className="gap-2">
          <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>
            {d.name && String(d.name).trim() ? d.name : "Milestone"}
          </FieldLabel>
          {!isEditing ? (
            <FieldDescription className={BROKER_FIELD_DESC_CLASS}>
              Threshold: <span className={accountSettingsFieldValueClassName}>{typeLabel}</span>
            </FieldDescription>
          ) : null}
        </Field>

        {!isEditing ? (
          <>
            {showProgressSection ? (
              <>
                <div className="mb-1 flex items-center justify-between gap-2">
                  <span className={BROKER_FIELD_DESC_CLASS}>Progress</span>
                  <span className={BROKER_FIELD_DESC_CLASS}>
                    {progressCurrent != null && progressTarget != null
                      ? `${formatProgressValue(progressCurrent)} / ${formatProgressValue(progressTarget)}`
                      : progressFractionForBar != null
                        ? `${Math.round(clampMilestoneProgress01(progressFractionForBar) * 100)}%`
                        : "—"}
                  </span>
                </div>
                <div
                  className="h-2 w-full overflow-hidden rounded-full bg-muted"
                  data-pnl-milestone-progress-track=""
                >
                  <div
                    className="h-full overflow-hidden rounded-full transition-all"
                    style={{ width: `${progressBarValue}%` }}
                  >
                    {reached ? (
                      <div className="h-full w-full rounded-full bg-[color:var(--tessera-pnl-positive)]" />
                    ) : gradientInnerWidthPercent != null ? (
                      <div
                        className="pnl-milestone-progress-gradient h-full rounded-full"
                        data-pnl-milestone-progress-gradient=""
                        style={{ width: `${gradientInnerWidthPercent}%` }}
                      />
                    ) : null}
                  </div>
                </div>
                {distance != null ? (
                  <FieldDescription className={BROKER_FIELD_DESC_CLASS}>
                    Distance to target:{" "}
                    <span className={accountSettingsFieldValueClassName}>
                      {formatProgressValue(distance)}
                    </span>
                  </FieldDescription>
                ) : null}
              </>
            ) : null}
          </>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
            <Field className="gap-2 md:col-span-2 xl:col-span-1">
              <FieldLabel htmlFor={`pnl-milestone-name-${m.id}`} className={BROKER_ALERT_TITLE_CLASS}>
                Name (optional)
              </FieldLabel>
              <Input
                id={`pnl-milestone-name-${m.id}`}
                value={(d.name ?? "") as string}
                onChange={(event) => onDraftChange({ name: event.target.value })}
                data-font="primary"
              />
            </Field>
            <Field className="gap-2">
              <FieldLabel
                htmlFor={`pnl-milestone-threshold-${m.id}`}
                className={BROKER_ALERT_TITLE_CLASS}
              >
                Threshold
              </FieldLabel>
              <Input
                id={`pnl-milestone-threshold-${m.id}`}
                type="number"
                value={Number(d.threshold_value ?? 0)}
                onChange={(event) =>
                  onDraftChange({ threshold_value: Number(event.target.value) })
                }
                data-font="primary"
              />
            </Field>
            <PnlMilestoneFieldSelect
              id={`pnl-milestone-scope-${m.id}`}
              label="Scope"
              value={(d.scope ?? m.scope) as string}
              disabled={isAuto}
              onValueChange={(value) => onDraftChange({ scope: value as PnlMilestoneScope })}
              options={PNL_MILESTONE_SCOPE_OPTIONS}
            />
            <PnlMilestoneFieldSelect
              id={`pnl-milestone-type-${m.id}`}
              label="Type"
              value={(d.milestone_type ?? m.milestone_type) as string}
              disabled={isAuto}
              onValueChange={(value) =>
                onDraftChange({ milestone_type: value as PnlMilestoneType })
              }
              options={PNL_MILESTONE_TYPE_OPTIONS}
            />
            <PnlMilestoneFieldSelect
              id={`pnl-milestone-direction-${m.id}`}
              label="Direction"
              value={(d.direction ?? m.direction) as string}
              disabled={isAuto}
              onValueChange={(value) =>
                onDraftChange({ direction: value as PnlMilestoneDirection })
              }
              options={PNL_MILESTONE_DIRECTION_OPTIONS}
            />
          </div>
        )}

        {showFooter ? (
      <div
        className={cn(
          "flex flex-wrap items-center gap-3 border-t border-border pt-3",
          showEnabledToggle ? "justify-between" : "justify-end"
        )}
      >
          {showEnabledToggle ? (
            <div className="flex items-center gap-2">
              <Switch
                id={`pnl-milestone-enabled-${m.id}`}
                checked={!Boolean(m.is_suppressed)}
                disabled={saving}
                onCheckedChange={(checked) => {
                  const enabled = checked === true;
                  void (async () => {
                    setSaving(true);
                    try {
                      await patchPnlMilestone(m.id, m.account_code, {
                        is_suppressed: !enabled,
                        ...(enabled ? { is_active: true } : { is_active: false }),
                      });
                      await onReloadQuiet(m.account_code);
                    } catch (err) {
                      presentMutationError(err, "Failed to update milestone");
                    } finally {
                      setSaving(false);
                    }
                  })();
                }}
              />
              <label
                htmlFor={`pnl-milestone-enabled-${m.id}`}
                className={`${BROKER_FIELD_DESC_CLASS} cursor-pointer font-normal leading-none`}
              >
                Enabled
              </label>
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-2">
            {!isEditing ? (
              <>
                {!isAuto ? (
                  <Button
                    type="button"
                    variant="outline-gray"
                    size="sm"
                    data-font="primary"
                    disabled={saving}
                    onClick={onBeginEdit}
                  >
                    <Pencil data-icon="inline-start" className="size-3.5" aria-hidden />
                    Edit
                  </Button>
                ) : null}
                {!isAuto ? (
                  <Button
                    type="button"
                    variant="outline-red"
                    size="sm"
                    data-font="primary"
                    disabled={saving}
                    onClick={onDelete}
                  >
                    <Trash2 data-icon="inline-start" className="size-3.5" aria-hidden />
                    Delete
                  </Button>
                ) : null}
              </>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline-gray"
                  size="sm"
                  data-font="primary"
                  disabled={saving}
                  onClick={onCancelEdit}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="gradient"
                  size="sm"
                  data-font="primary"
                  disabled={saving}
                  aria-busy={saving || undefined}
                  onClick={onSaveEdit}
                >
                  {saving ? (
                    <Loader2 className="size-3.5 shrink-0 animate-spin" aria-hidden />
                  ) : null}
                  Save
                </Button>
              </>
            )}
          </div>
        </div>
      ) : null}
      </div>
    </div>
  );
}
